/**
 * Permission requirements for the global "Quick Add" (+) actions.
 *
 * Each quick-add action maps to the module whose `add` permission a user must
 * hold to see and use it. The Quick Add panel/menu filters its items through
 * this map so a user is only ever offered create actions they're actually
 * allowed to perform — the UI mirror of the backend RLS. Without it every role
 * (including self-service `member`/`volunteer` portal users) saw "Add Person",
 * "Record Contribution", etc.
 *
 * Keyed by the action id used in `QuickAddPanel` (which matches the keys used
 * in `QuickAddMenu`). Every action is gated on the `add` action of its module.
 */
import type { Module } from "@/lib/permissions";

export const QUICK_ADD_MODULE = {
  "add-person": "people",
  "create-request": "requests",
  "record-contribution": "contributions",
  "create-event": "calendar",
  "create-service": "services",
  "post-announcement": "announcements",
  "create-project": "projects",
  "create-message": "messaging",
  // Reimbursement is filed as a request, so it follows the requests module.
  "create-reimbursement": "requests",
} satisfies Record<string, Module>;

export type QuickAddActionId = keyof typeof QUICK_ADD_MODULE;

/** Distinct modules referenced by any quick-add action. */
export const QUICK_ADD_MODULES: Module[] = Array.from(
  new Set(Object.values(QUICK_ADD_MODULE)),
) as Module[];
